import { useNode, useEditor } from "@craftjs/core";
import React, { useContext } from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  BoxShadowInput,
  MarginInput,
  Panel,
  Tabs,
  NumberInput,
  BorderRadiusInput,
  BorderInput,
  ToggleInput,
  TextInput,
  MultipleSelect,
  DateInput,
  PaddingInput,
} from "../../input";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { remove_prefix } from "../../helpers";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const CategorySettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node, parseFreshNode },
    actions: { add, delete: deleteCraftJSNode, setProp: setPropGlobal },
  } = useEditor();

  const parents = node(id).ancestors();

  const { project, projectCategories } = useContext(GrigoraDashboardContext);

  const {
    categoryQuery,
    columns,
    columnsTablet = 1,
    columnsMobile = 1,
    blockGap = "1rem",
    blockGapTablet,
    blockGapMobile,
    textColor,
    backgroundColor,
    typoSize,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoWeight,
    typoTransform,
    typoDecoration,
    typoStyle,
    border,
    borderRadius,
    layoutPadding,
    width,
    maxWidth,
    minWidth,
    alignItems,
    boxShadow,
  } = props;

  const ALIGNITEMS = [
    {
      label: "Start",
      value: "start",
    },
    {
      label: "Center",
      value: "center",
    },
    {
      label: "End",
      value: "end",
    },
  ];

  const ORDERBY = [
    {
      label: "Title",
      value: "name",
    },
    {
      label: "Slug",
      value: "slug",
    },
    {
      label: "Number of Posts",
      value: "postsCount",
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: categoryQuery }])}
          id={`block-setting-${id}-settings`}
          title={"Settings"}
          initialOpen={true}
        >
          <NumberInput
            isResetVisible={false}
            max={INPUT_LIMITS["number-of-categories"].max}
            min={INPUT_LIMITS["number-of-categories"].min}
            step={INPUT_LIMITS["number-of-categories"].step}
            value={categoryQuery?.limit || 10}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  limit: val,
                };
              });
            }}
            label={"No. of Category"}
          />
          <NumberInput
            isResetVisible={false}
            max={INPUT_LIMITS["category-offset"].max}
            min={INPUT_LIMITS["category-offset"].min}
            step={INPUT_LIMITS["category-offset"].step}
            value={categoryQuery?.offset || 0}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  offset: val,
                };
              });
            }}
            label={"Offset"}
          />
          <SelectInput
            isResetVisible={false}
            value={categoryQuery?.order}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  order: val,
                };
              });
            }}
            options={[
              { label: "Ascending", value: "asc" },
              { label: "Descending", value: "desc" },
            ]}
            label="Order"
          />
          <SelectInput
            isResetVisible={false}
            value={categoryQuery?.orderBy}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  orderBy: val,
                };
              });
            }}
            options={ORDERBY}
            label="Order By"
          />
          <TextInput
            minLength={INPUT_LIMITS["search"].minlength}
            maxLength={INPUT_LIMITS["search"].maxlength}
            isResetVisible={false}
            value={categoryQuery?.search}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  search: val,
                };
              });
            }}
            label={"Search"}
          />
          <MultipleSelect
            value={categoryQuery?.categories}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  categories: val,
                };
              });
            }}
            options={projectCategories.map((item) => {
              return {
                label: item.name,
                value: remove_prefix(item.sk, "cms-category-"),
              };
            })}
            label={"Categories"}
          />
          <MultipleSelect
            value={categoryQuery?.excludeCategories}
            onChange={(val) => {
              setProp((props) => {
                props.categoryQuery = {
                  ...(props.categoryQuery ? props.categoryQuery : {}),
                  excludeCategories: val,
                };
              });
            }}
            options={projectCategories.map((item) => {
              return {
                label: item.name,
                value: remove_prefix(item.sk, "cms-category-"),
              };
            })}
            label={"Exclude Categories"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: columns },
            { key: columnsTablet, default: 1 },
            { key: columnsMobile, default: 1 },
            { key: blockGap, default: "1rem" },
            { key: blockGapTablet },
            { key: blockGapMobile },
            { key: width },
            { key: maxWidth },
            { key: alignItems },
          ])}
          id={`block-setting-${id}-dimensions`}
          title={"Dimensions"}
          initialOpen={false}
        >
          <SelectInput
            isResetVisible={false}
            value={columns}
            onChange={(val) => {
              setProp((props) => {
                props.columns = val;
              });
            }}
            options={Array.from({ length: 6 }, (_, i) => {
              return {
                label: i + 1,
                value: i + 1,
              };
            })}
            min={1}
            max={4}
            step={1}
            label={"Columns"}
            isResponsive={true}
            valueTablet={columnsTablet}
            valueMobile={columnsMobile}
            setValueTablet={(val) => {
              setProp((props) => {
                props.columnsTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.columnsMobile = val;
              });
            }}
          />
          <UnitInput
            value={blockGap}
            onChange={(val) => {
              setProp((props) => {
                props.blockGap = val;
              });
            }}
            resetValue={"1rem"}
            label={"Gap"}
            isResponsive={true}
            valueTablet={blockGapTablet}
            valueMobile={blockGapMobile}
            setValueTablet={(val) => {
              setProp((props) => {
                props.blockGapTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.blockGapMobile = val;
              });
            }}
          />
          <UnitInput
            auto
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            value={maxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            value={minWidth}
            onChange={(val) => {
              setProp((props) => {
                props.minWidth = val;
              });
            }}
            label={"Min Width"}
          />
          <SelectInput
            value={alignItems}
            onChange={(val) => {
              setProp((props) => {
                props.alignItems = val;
              });
            }}
            options={ALIGNITEMS.map(function (item) {
              return {
                label: item.label,
                value: item.value,
              };
            })}
            label="Vertical Align"
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            value={typoSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={["uppercase", "lowercase", "capitalize", "none"].map(
              function (item) {
                return {
                  label: item,
                  value: item,
                };
              },
            )}
            label="Transform"
          />
          <SelectInput
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={["underline", "overline", "none"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Decoration"
          />
          <SelectInput
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={["normal", "italic"].map(function (item) {
              return {
                label: item,
                value: item,
              };
            })}
            label="Style"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: backgroundColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <ColorInput
              value={textColor}
              onChange={(val) => {
                setProp((props) => {
                  props.textColor = val;
                });
              }}
              label={"Text"}
            />
            <ColorInput
              value={backgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.backgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: layoutPadding }])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <PaddingInput
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <BorderInput
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-box-shadow`}
          title={"Box Shadow"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: categoryQuery },
        { key: columns },
        { key: columnsTablet, default: 1 },
        { key: columnsMobile, default: 1 },
        { key: blockGap, default: "1rem" },
        { key: blockGapTablet },
        { key: blockGapMobile },
        { key: width },
        { key: maxWidth },
        { key: alignItems },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: typoSize },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutPadding },
        { key: border },
        { key: borderRadius },
        { key: boxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
